import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../lib/toast";
import type { DiscoveredMeet } from "../api/types";

/**
 * Admin-only banner pinned at the top of the app. When discovered meets are
 * awaiting review, it surfaces them everywhere an admin goes (not just the
 * Reviews page) and lets them approve/reject the top items inline — so the
 * queue gets actioned instead of piling up. Hidden entirely for non-admins and
 * when the queue is empty.
 */
export function ReviewAlert() {
  const { isAdmin } = useAuth();
  const toast = useToast();
  const [items, setItems] = useState<DiscoveredMeet[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [busyId, setBusyId] = useState<string>();
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await api.reviewQueue();
      setItems(res.items);
    } catch {
      // Silent — the banner is non-critical.
    } finally {
      setLoaded(true);
    }
  }, [isAdmin]);

  useEffect(() => {
    load();
    // Light polling so newly-crawled items surface without a manual refresh.
    const t = window.setInterval(load, 60_000);
    return () => window.clearInterval(t);
  }, [load]);

  const act = async (id: string, action: "approve" | "reject") => {
    setBusyId(id);
    // Optimistic: remove from the banner immediately.
    const prev = items;
    setItems((xs) => xs.filter((m) => m.discoveryId !== id));
    try {
      if (action === "approve") await api.approveDiscovered(id);
      else await api.rejectDiscovered(id);
      toast.success(action === "approve" ? "Approved & published" : "Rejected");
    } catch (e) {
      setItems(prev); // roll back
      toast.error((e as Error).message);
    } finally {
      setBusyId(undefined);
    }
  };

  if (!isAdmin || !loaded || items.length === 0) return null;

  const top = items.slice(0, expanded ? items.length : 3);

  return (
    <div className="review-alert" role="region" aria-label="Pending meet reviews">
      <div className="review-alert-head">
        <span className="review-alert-badge">{items.length}</span>
        <strong>
          {items.length} meet{items.length === 1 ? "" : "s"} awaiting review
        </strong>
        <span className="review-alert-spacer" />
        <Link to="/discovery/review" className="link-strong">
          Open queue →
        </Link>
      </div>

      <div className="review-alert-list">
        {top.map((m) => (
          <div key={m.discoveryId} className="review-alert-item">
            <div className="review-alert-info">
              <strong>{m.title}</strong>
              <span className="muted">
                {m.startDate}
                {m.city ? ` · ${m.city}` : ""} · {m.state} · conf{" "}
                {Math.round(m.confidence * 100)}% · {m.sourceName}
              </span>
            </div>
            <div className="review-alert-actions">
              <button
                className="btn-sm"
                disabled={busyId === m.discoveryId}
                onClick={() => act(m.discoveryId, "approve")}
              >
                Approve
              </button>
              <button
                className="btn-sm danger"
                disabled={busyId === m.discoveryId}
                onClick={() => act(m.discoveryId, "reject")}
              >
                Reject
              </button>
            </div>
          </div>
        ))}
      </div>

      {items.length > 3 && (
        <button
          className="secondary btn-sm review-alert-more"
          onClick={() => setExpanded((e) => !e)}
        >
          {expanded ? "Show less" : `Show ${items.length - 3} more`}
        </button>
      )}
    </div>
  );
}
